/**
 * Base HTTP client for the optum-agent API.
 *
 * Auth model: bearer access token from the OpenID Connect realm. The UI
 * installs a token provider (or a static token via `setAuthToken()`); we
 * read it per-request and attach `Authorization: Bearer <token>`. No
 * cookies, no CSRF.
 *
 * Error model: RFC 7807 Problem Details
 *   { type, title, status, detail, extras? }
 * mapped into the single typed `ApiError` class below.
 *
 * Consumer bootstrap (main.tsx):
 *   configureApiClient({ baseUrl: window.location.origin });
 */

// ─────────────────────────── Config ───────────────────────────

interface ClientConfig {
  baseUrl: string;
}

let config: ClientConfig = { baseUrl: 'http://localhost:8000' };

/** Set the API base URL. Call once at app startup. */
export function configureApiClient(next: Partial<ClientConfig>): void {
  config = { ...config, ...next };
}

export function getApiClientConfig(): Readonly<ClientConfig> {
  return config;
}

// ────────────────────────── Auth token ────────────────────────

// Two resolution modes, checked in order per-request:
//   1) A dynamic provider (the realm session's access token) — the identity
//      library refreshes short-lived tokens behind it.
//   2) A static token set via `setAuthToken()` — for tests and service tokens,
//      where refresh is unnecessary.
type TokenProvider = () => string | null | Promise<string | null>;

let authToken: string | null = null;
let tokenProvider: TokenProvider | null = null;

/** Set (or clear) the static bearer token used by every subsequent request. */
export function setAuthToken(token: string | null): void {
  authToken = token && token.trim().length > 0 ? token.trim() : null;
}

export function getAuthToken(): string | null {
  return authToken;
}

/**
 * Install a dynamic token provider. When set, it is called per-request and
 * its return value replaces the static `authToken`. Pass `null` to remove.
 */
export function setTokenProvider(provider: TokenProvider | null): void {
  tokenProvider = provider;
}

/** The bearer the next request would carry: the provider's token, else the static one. */
export async function resolveAuthToken(): Promise<string | null> {
  if (tokenProvider) {
    const value = await tokenProvider();
    if (value && value.trim().length > 0) return value.trim();
  }
  return authToken;
}

// ─────────────────────────── ApiError ──────────────────────────

export interface ApiErrorInit {
  code: string;
  message: string;
  status: number;
  details?: Record<string, unknown>;
}

/** Typed error thrown for any non-2xx response (and network failures). */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    if (init.details !== undefined) this.details = init.details;
  }
}

// ─────────────────────────── Fetch core ─────────────────────────

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type QueryParams = Record<string, string | number | boolean | null | undefined>;

interface RequestOptions {
  body?: unknown;
  query?: QueryParams;
  /** Extra headers — overrides defaults if keys collide. */
  headers?: Record<string, string>;
  /** If set, `body` is sent as-is (FormData, Blob, …) — no JSON.stringify. */
  rawBody?: BodyInit;
}

function buildUrl(path: string, query?: QueryParams): string {
  const url = new URL(path, config.baseUrl);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}

/** A parsed response body together with the response it came in. */
export interface RawResult<T> {
  body: T;
  headers: Headers;
}

async function request<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  return (await requestRaw<T>(method, path, options)).body;
}

async function requestRaw<T>(
  method: HttpMethod,
  path: string,
  options: RequestOptions = {},
): Promise<RawResult<T>> {
  const url = buildUrl(path, options.query);

  const token = await resolveAuthToken();
  const headers: Record<string, string> = {
    Accept: 'application/json',
    // JSON content-type only when we're JSON-encoding a body ourselves.
    ...(options.body !== undefined ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };

  let response: Response;
  try {
    response = await fetch(url, {
      method,
      headers,
      body: options.rawBody ?? (options.body !== undefined ? JSON.stringify(options.body) : null),
    });
  } catch (err) {
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: err instanceof Error ? err.message : 'Network request failed',
      status: 0,
    });
  }

  if (response.status === 204) return { body: undefined as T, headers: response.headers };

  const text = await response.text();
  const payload: unknown = text.length > 0 ? safeJsonParse(text) : undefined;

  if (response.ok) return { body: payload as T, headers: response.headers };
  throw problemError(response, payload);
}

/** The ApiError a failed response stands for, from its RFC 7807 body when it has one. */
function problemError(response: Response, payload: unknown): ApiError {
  // RFC 7807 problem details (FastAPI default: { type, title, status, detail, extras? }).
  // Also handle FastAPI validation errors: { detail: [ValidationError, ...] }.
  const problem = payload as
    | { title?: string; detail?: unknown; extras?: Record<string, unknown> }
    | undefined;
  const detail = problem?.detail;
  const message =
    typeof detail === 'string'
      ? detail
      : (problem?.title ?? `Request failed with status ${response.status}`);
  // RFC 7807 extension members (e.g. `reason`) ride top-level; keep them
  // reachable so callers can branch on machine-readable causes.
  const known = new Set(['type', 'title', 'status', 'detail', 'instance', 'extras']);
  const extensions = Object.fromEntries(
    Object.entries((payload as Record<string, unknown>) ?? {}).filter(([key]) => !known.has(key)),
  );
  const details =
    problem?.extras ??
    (Array.isArray(detail)
      ? { validation: detail }
      : Object.keys(extensions).length > 0
        ? extensions
        : undefined);
  return new ApiError({
    code: deriveCode(response.status, problem?.title),
    message,
    status: response.status,
    ...(details !== undefined ? { details: details as Record<string, unknown> } : {}),
  });
}

/** A file a route sends as an attachment: the bytes and the name it was sent under. */
export interface BlobResult {
  blob: Blob;
  filename: string;
}

const FILENAME = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i;

/** The filename of a Content-Disposition header, or the fallback when it names none. */
export function filenameFrom(disposition: string | null, fallback: string): string {
  const match = disposition ? FILENAME.exec(disposition) : null;
  return match?.[1] ? decodeURIComponent(match[1]) : fallback;
}

/** GET a download: the same URL, token, and errors as apiGet, a Blob instead of JSON. */
export async function apiGetBlob(
  path: string,
  query?: QueryParams,
  fallbackName = 'download',
): Promise<BlobResult> {
  const url = buildUrl(path, query);
  const token = await resolveAuthToken();
  let response: Response;
  try {
    response = await fetch(url, {
      method: 'GET',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  } catch (err) {
    throw new ApiError({
      code: 'NETWORK_ERROR',
      message: err instanceof Error ? err.message : 'Network request failed',
      status: 0,
    });
  }
  if (!response.ok) {
    const text = await response.text();
    throw problemError(response, text.length > 0 ? safeJsonParse(text) : undefined);
  }
  return {
    blob: await response.blob(),
    filename: filenameFrom(response.headers.get('Content-Disposition'), fallbackName),
  };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { detail: text };
  }
}

/** Map an HTTP status to a stable code string for UI branching. */
function deriveCode(status: number, title?: string): string {
  if (title) return title.toUpperCase().replace(/[^A-Z0-9]+/g, '_');
  switch (status) {
    case 400:
      return 'BAD_REQUEST';
    case 401:
      return 'UNAUTHENTICATED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 409:
      return 'CONFLICT';
    case 410:
      return 'GONE';
    case 413:
      return 'PAYLOAD_TOO_LARGE';
    case 422:
      return 'VALIDATION_ERROR';
    case 429:
      return 'RATE_LIMITED';
    case 500:
      return 'SERVER_ERROR';
    default:
      return `HTTP_${status}`;
  }
}

// ─────────────────────── Method convenience ────────────────────

/**
 * One `event:`/`data:` block from a server-sent-event stream.
 *
 * A comment line — the keepalive — carries no event and is not reported.
 */
export interface ServerSentEvent {
  event: string;
  data: string;
}

/**
 * Read a server-sent-event stream, calling `onEvent` for each block.
 *
 * `fetch` rather than `EventSource`: the browser API cannot carry an
 * Authorization header, and moving the credential into the query string
 * would write it into every access log and referrer along the way.
 *
 * Resolves when the server closes the stream. Abort through `signal` to
 * stop early; that rejects with the abort reason, as `fetch` does.
 */
export async function apiEventStream(
  path: string,
  onEvent: (event: ServerSentEvent) => void,
  options: { signal?: AbortSignal; query?: QueryParams } = {},
): Promise<void> {
  const token = await resolveAuthToken();
  const response = await fetch(buildUrl(path, options.query), {
    method: 'GET',
    headers: {
      Accept: 'text/event-stream',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    ...(options.signal ? { signal: options.signal } : {}),
  });
  if (!response.ok || !response.body) {
    const text = await response.text().catch(() => '');
    const problem = (text.length > 0 ? safeJsonParse(text) : undefined) as
      | { title?: string; detail?: unknown }
      | undefined;
    throw new ApiError({
      code: deriveCode(response.status, problem?.title),
      message:
        typeof problem?.detail === 'string'
          ? problem.detail
          : (problem?.title ?? `Request failed with status ${response.status}`),
      status: response.status,
    });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // Blocks are separated by a blank line; the last piece may be a
      // partial block and stays in the buffer until the rest arrives.
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop() ?? '';
      for (const block of blocks) {
        const parsed = parseEventBlock(block);
        if (parsed) onEvent(parsed);
      }
    }
  } finally {
    reader.releaseLock();
  }
}

function parseEventBlock(block: string): ServerSentEvent | null {
  let event = 'message';
  const data: string[] = [];
  for (const line of block.split('\n')) {
    // A comment. The server sends these to keep an idle proxy from
    // closing the connection; they mean nothing to a reader.
    if (line.startsWith(':')) continue;
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) data.push(line.slice(5).trim());
  }
  return data.length > 0 ? { event, data: data.join('\n') } : null;
}

export function apiGet<T>(path: string, query?: QueryParams): Promise<T> {
  return request<T>('GET', path, query ? { query } : {});
}

/** `apiGet`, keeping the response headers for a caller that reads one. */
export function apiGetWithHeaders<T>(path: string, query?: QueryParams): Promise<RawResult<T>> {
  return requestRaw<T>('GET', path, query ? { query } : {});
}

export function apiPost<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('POST', path, body !== undefined ? { body } : {});
}

export function apiPatch<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PATCH', path, body !== undefined ? { body } : {});
}

export function apiPut<T>(path: string, body?: unknown): Promise<T> {
  return request<T>('PUT', path, body !== undefined ? { body } : {});
}

export function apiDelete<T>(path: string, query?: QueryParams): Promise<T> {
  return request<T>('DELETE', path, query ? { query } : {});
}

/** POST a multipart/form-data body (wheel upload). */
export function apiPostForm<T>(path: string, form: FormData): Promise<T> {
  return request<T>('POST', path, { rawBody: form });
}
